class MozCategoryTabs extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-category]'));
    this.cards = Array.from(this.querySelectorAll('[data-grid-card]'));
    this.panel = this.querySelector('[data-category-panel]');
    this.emptyState = this.querySelector('[data-category-empty]');
    this.tablist = this.querySelector('[role="tablist"]');
    this.viewAllLink = this.querySelector('.products-grid__button');
    this.limit = Number.parseInt(this.dataset.limit, 10) || this.cards.length;
    this.mobileLimit = Number.parseInt(this.dataset.mobileLimit, 10) || Math.min(6, this.limit);
    this.priorityRowSize = Number.parseInt(this.dataset.columns, 10) || 4;
    this.mobilePriorityRowSize = Number.parseInt(this.dataset.mobileColumns, 10) || 2;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.imageWarmObserver = null;
    this.imageWarmHandle = 0;
    this.imageWarmUsesIdle = false;
    this.imageWarmToken = 0;
    this.hasEnteredWarmZone = false;
    this.lastPageScrollAt = 0;
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handlePageScroll = this.handlePageScroll.bind(this);

    this.cards.forEach((card, index) => {
      card.querySelectorAll('img').forEach((image) => {
        image.decoding = 'async';
        image.fetchPriority = !card.hidden && index < this.priorityRowSize ? 'auto' : 'low';
      });
    });

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleClick);
      tab.addEventListener('keydown', this.handleKeydown);
    });

    if (typeof this.mobileQuery.addEventListener === 'function') {
      this.mobileQuery.addEventListener('change', this.handleViewportChange);
    } else {
      this.mobileQuery.addListener(this.handleViewportChange);
    }

    const initialTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
    if (initialTab) this.activate(initialTab, { animate: false });

    this.classList.add('is-ui-enhanced');
    if ('IntersectionObserver' in window) {
      this.revealObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-ui-ready');
        this.revealObserver?.disconnect();
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      this.revealObserver.observe(this);
    } else {
      this.classList.add('is-ui-ready');
    }

    window.addEventListener('scroll', this.handlePageScroll, { passive: true });
    this.setupImageWarmup();
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.handleClick);
      tab.removeEventListener('keydown', this.handleKeydown);
    });

    if (typeof this.mobileQuery?.removeEventListener === 'function') {
      this.mobileQuery.removeEventListener('change', this.handleViewportChange);
    } else {
      this.mobileQuery?.removeListener(this.handleViewportChange);
    }

    this.revealObserver?.disconnect();
    this.imageWarmObserver?.disconnect();
    window.removeEventListener('scroll', this.handlePageScroll);
    this.cancelImageWarmHandle();
    this.imageWarmToken += 1;
    this.panelAnimation?.cancel();
    this.isInitialized = false;
  }

  cancelImageWarmHandle() {
    if (!this.imageWarmHandle) return;
    if (this.imageWarmUsesIdle && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.imageWarmHandle);
    } else {
      window.clearTimeout(this.imageWarmHandle);
    }
    this.imageWarmHandle = 0;
    this.imageWarmUsesIdle = false;
  }

  scheduleImageWarmBatch(callback) {
    this.cancelImageWarmHandle();
    const runWhenSettled = () => {
      this.imageWarmHandle = 0;
      this.imageWarmUsesIdle = false;

      const quietFor = performance.now() - this.lastPageScrollAt;
      if (quietFor < 180) {
        this.imageWarmHandle = window.setTimeout(
          runWhenSettled,
          Math.ceil(180 - quietFor) + 24
        );
        return;
      }

      callback();
    };

    if ('requestIdleCallback' in window) {
      this.imageWarmUsesIdle = true;
      this.imageWarmHandle = window.requestIdleCallback(runWhenSettled, { timeout: 1500 });
    } else {
      this.imageWarmUsesIdle = false;
      this.imageWarmHandle = window.setTimeout(runWhenSettled, 72);
    }
  }

  handlePageScroll() {
    this.lastPageScrollAt = performance.now();
  }

  warmVisibleImages() {
    const token = ++this.imageWarmToken;
    const images = this.cards
      .filter((card) => !card.hidden)
      .map((card) => card.querySelector('img.media--first'))
      .filter((image) => image && image.dataset.mozWarmReady !== 'true');
    let index = 0;

    const warmNextBatch = () => {
      if (token !== this.imageWarmToken || index >= images.length) return;
      const batch = images.slice(index, index + 2);
      index += batch.length;

      const decodes = batch.map((image) => {
        image.dataset.mozWarmReady = 'true';
        image.loading = 'eager';
        image.decoding = 'async';
        return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
      });

      Promise.allSettled(decodes).then(() => {
        if (token === this.imageWarmToken && index < images.length) {
          this.scheduleImageWarmBatch(warmNextBatch);
        }
      });
    };

    if (images.length) this.scheduleImageWarmBatch(warmNextBatch);
  }

  setupImageWarmup() {
    if (!('IntersectionObserver' in window)) {
      this.hasEnteredWarmZone = true;
      this.warmVisibleImages();
      return;
    }

    this.imageWarmObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.hasEnteredWarmZone = true;
      this.warmVisibleImages();
      this.imageWarmObserver?.disconnect();
      this.imageWarmObserver = null;
    }, { rootMargin: '110% 0px', threshold: 0 });
    this.imageWarmObserver.observe(this);
  }

  handleClick(event) {
    this.activate(event.currentTarget, { centerTab: true });
  }

  handleKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;

    this.tabs[nextIndex].focus();
    this.activate(this.tabs[nextIndex], { centerTab: true });
  }

  handleViewportChange() {
    if (!this.activeTab) return;
    this.activate(this.activeTab, { animate: false, force: true, centerTab: true });
  }

  getCurrentLimit() {
    return this.mobileQuery.matches ? this.mobileLimit : this.limit;
  }

  getPriorityRowSize() {
    return this.mobileQuery.matches ? this.mobilePriorityRowSize : this.priorityRowSize;
  }

  centerTab(activeTab, animate) {
    if (!this.mobileQuery.matches || !this.tablist || !activeTab) return;

    const targetLeft = activeTab.offsetLeft - ((this.tablist.clientWidth - activeTab.offsetWidth) / 2);
    this.tablist.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto'
    });
  }

  activate(activeTab, { animate = true, force = false, centerTab = false } = {}) {
    if (!activeTab || (!force && this.activeTab === activeTab && this.hasActivated)) return;

    const category = activeTab.dataset.category;
    const currentLimit = this.getCurrentLimit();
    const currentPriorityRowSize = this.getPriorityRowSize();
    const categoryChanged = this.activeTab && this.activeTab !== activeTab;
    let visibleCount = 0;

    this.activeTab = activeTab;
    this.hasActivated = true;
    this.panel?.setAttribute('aria-busy', 'true');

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.classList.toggle('is-activating', isActive && animate && !this.reducedMotion.matches);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;

      if (isActive && animate && !this.reducedMotion.matches) {
        tab.addEventListener('animationend', () => tab.classList.remove('is-activating'), { once: true });
      }
    });

    this.cards.forEach((card) => {
      const categories = card.dataset.categories
        ? card.dataset.categories.split('|').map((value) => value.trim()).filter(Boolean)
        : [];
      const matches = category === 'all' || categories.includes(category);
      const shouldShow = matches && visibleCount < currentLimit;

      card.hidden = !shouldShow;
      card.querySelectorAll('img').forEach((image) => {
        image.fetchPriority = shouldShow && visibleCount < currentPriorityRowSize ? 'auto' : 'low';
      });
      if (shouldShow) visibleCount += 1;
    });

    if (this.panel) this.panel.setAttribute('aria-labelledby', activeTab.id);
    if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;

    if (categoryChanged && this.mobileQuery.matches && this.panel) {
      this.panel.scrollTo({ left: 0, behavior: 'auto' });
    }

    if (this.viewAllLink) {
      const categoryUrl = activeTab.dataset.categoryUrl;
      if (categoryUrl) {
        this.viewAllLink.href = categoryUrl;
        this.viewAllLink.removeAttribute('aria-disabled');
      }

      const categoryLabel = activeTab.textContent.trim();
      this.viewAllLink.setAttribute('aria-label', `${this.viewAllLink.textContent.trim()}: ${categoryLabel}`);
    }

    if (centerTab) this.centerTab(activeTab, animate);

    if (animate && this.panel && !this.reducedMotion.matches && typeof this.panel.animate === 'function') {
      this.panelAnimation?.cancel();
      this.panelAnimation = this.panel.animate(
        [
          { opacity: 0.64, transform: 'translate3d(0, 8px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ],
        { duration: 240, easing: 'cubic-bezier(0.2, 0.75, 0.2, 1)' }
      );
    }

    window.requestAnimationFrame(() => this.panel?.setAttribute('aria-busy', 'false'));

    if (this.hasEnteredWarmZone) this.warmVisibleImages();

    this.dispatchEvent(new CustomEvent('moz:category-change', {
      bubbles: true,
      detail: { category, visibleCount, limit: currentLimit }
    }));
  }
}

if (!customElements.get('moz-category-tabs')) {
  customElements.define('moz-category-tabs', MozCategoryTabs);
}
